import React, { Component } from 'react'
import { find, isEmpty, delay } from 'lodash'
import { t, Trans } from '@lingui/macro'
import classNames from 'classnames'

import {
  TopicElementModelAsEditor,
  TopicModelAsEditor,
  TopicVersionModelAsEditor
} from 'front-end/src/graphql/private/PrivateSchemaTypes'

import { IUploadProgress } from '../../../types/IUploadProgress'
import { MediaElement } from '../../components/MediaElement'
import CustomVideoInput from './components/CustomVideoInput'
import YoutubeVideoInput from './components/YoutubeVideoInput'
import Spinner, {
  SpinnerSizeEnum
} from '../../../../../../components/common/spinner/Spinner'
import IconRefresh from '../../../../../../components/common/icon/IconRefresh'
import getAWSSignatureV4Form from '../../../../../../utils/getAWSSignatureV4Form'
import { IUploadCredentials } from '../../../../../account/types'
import { apolloClientPrivate } from '../../../../../../graphql/apolloClient'
import {
  FetchEditVideoElementQuery,
  FetchEditVideoElementQueryVariables,
  FetchEditVideoElementDocument
} from './fetchEditVideoElement.codegen'
import {
  UpdateAudioElementDurationMutation,
  UpdateAudioElementDurationMutationVariables,
  UpdateAudioElementDurationDocument,
  FetchUploadCredentialsDocument,
  FetchUploadCredentialsQuery,
  FetchUploadCredentialsQueryVariables,
  RequestTranscodingDocument,
  RequestTranscodingMutation,
  RequestTranscodingMutationVariables,
  UpdateAudioElementContentDocument,
  UpdateAudioElementContentMutation,
  UpdateAudioElementContentMutationVariables,
  UpdateVideoElementContentDocument,
  UpdateVideoElementContentMutation,
  UpdateVideoElementContentMutationVariables,
  ReplaceElementDocument,
  ReplaceElementMutationVariables,
  ReplaceElementMutation
} from './EditModeVideo.codegen'
import { Box, ButtonLight } from 'front-end/src/components'
import {
  EditModeVideoProgressHeading as Heading,
  EditModeVideoProgressRow as Row,
  EditModeVideoProgressWrapper as Wrapper
} from './EditModeVideoProgress'
import { brandColors } from 'shared/basicPalette'
import GeneralRemoveModal from '../../../../../../components/common/general-remove-modal/GeneralRemoveModal'

interface IState {
  loading: boolean
  element: Partial<TopicElementModelAsEditor> | null | undefined
  editing: boolean
  uploadProgress?: IUploadProgress
  showReplaceModal: boolean
}

interface IElementProps {
  workspaceId: number
  topicId: number
  elementId: number
  fetchTopic: () => void
}

// TODO refactor to FC
class EditModeVideo extends Component<IElementProps, IState> {
  state: IState = {
    loading: true,
    editing: false,
    element: null,
    showReplaceModal: false
  }
  uploadUrl: string
  params: IUploadCredentials

  get isTranscoding() {
    const { element, editing, loading } = this.state

    if (!element) {
      return true
    }

    if (this.params && editing && loading) {
      return true
    }

    if (element.documents && isEmpty(element.documents)) {
      return false
    }

    return !find(
      element.documents,
      ({ status }) => status && ['Error', 'Complete'].includes(status)
    )
  }

  componentDidMount() {
    this.fetchElement()
  }

  componentDidUpdate({ elementId, topicId, workspaceId }: IElementProps) {
    if (
      elementId !== this.props.elementId ||
      topicId !== this.props.topicId ||
      workspaceId !== this.props.workspaceId
    ) {
      this.setState({ loading: true })
      this.fetchElement()
    }

    if (this.isTranscoding) {
      delay(() => {
        this.fetchElement()
      }, 5000)
    }
  }

  async fetchElement() {
    const { elementId, topicId } = this.props

    const { data } = await apolloClientPrivate.query<
      FetchEditVideoElementQuery,
      FetchEditVideoElementQueryVariables
    >({
      query: FetchEditVideoElementDocument,
      fetchPolicy: 'network-only',
      variables: {
        elementId,
        topicId
      }
    })

    const topic = data.topic as TopicModelAsEditor

    if (topic.draftVersion) {
      const { element } = topic.draftVersion
      if (element) {
        const { documents } = element
        const { content: youtubeUrl } = element.data || {}

        this.setState({
          element: element,
          loading: false,
          editing: !youtubeUrl && isEmpty(documents)
        })
      }
    }
  }

  handleYoutubeUrlSelected = async (url: string) => {
    const { elementId, topicId, fetchTopic } = this.props

    this.setState({
      loading: true
    })
    const variables = {
      elementId,
      topicId,
      url
    }

    try {
      const response = await apolloClientPrivate.mutate<
        UpdateVideoElementContentMutation,
        UpdateVideoElementContentMutationVariables
      >({
        variables,
        mutation: UpdateVideoElementContentDocument
      })

      const topic = response.data?.topic as TopicModelAsEditor

      const element = topic.draftVersion?.element?.patchContent
      this.setState({
        element: element as TopicElementModelAsEditor,
        editing: false
      })
    } finally {
      this.setState({
        loading: false
      })
      await fetchTopic()
    }
  }

  onUploadProgress = async (uploadProgress: IUploadProgress) => {
    this.setState({ uploadProgress })
  }

  onUploadComplete = async (filePath: string) => {
    this.setState({ uploadProgress: undefined, loading: true })
    const { elementId, topicId, fetchTopic } = this.props
    if (filePath.endsWith('mp3')) {
      const { data } = await apolloClientPrivate.mutate<
        UpdateAudioElementContentMutation,
        UpdateAudioElementContentMutationVariables
      >({
        mutation: UpdateAudioElementContentDocument,
        variables: {
          elementId,
          topicId,
          url: filePath
        }
      })

      const topic = data?.topic as TopicModelAsEditor
      const draftVersion = topic?.draftVersion as TopicVersionModelAsEditor

      const { element } = draftVersion
      this.setState({
        editing: false,
        loading: false,
        element: element?.setMp3ContentUrl as TopicElementModelAsEditor
      })
      const audio = document.createElement('audio')
      if (!element?.setMp3ContentUrl?.content) {
        throw new Error('there was a problem uploading mp3 file')
      }
      audio.src = element?.setMp3ContentUrl?.content
      audio.addEventListener(
        'loadedmetadata',
        async () => {
          await apolloClientPrivate.mutate<
            UpdateAudioElementDurationMutation,
            UpdateAudioElementDurationMutationVariables
          >({
            mutation: UpdateAudioElementDurationDocument,
            variables: {
              elementId,
              topicId,
              duration: Math.ceil(audio.duration)
            }
          })
          fetchTopic()
        },
        false
      )
    } else {
      const variables = {
        elementId,
        topicId,
        filePath
      }
      const response = await apolloClientPrivate.mutate<
        RequestTranscodingMutation,
        RequestTranscodingMutationVariables
      >({
        variables,
        mutation: RequestTranscodingDocument
      })

      const topic = response.data?.topic as TopicModelAsEditor

      // we don't need to transcode mp3
      const { ...element } = topic.draftVersion?.element?.requestTranscoding

      this.setState({
        editing: false,
        loading: false,
        element: element as any as TopicElementModelAsEditor
      })
      fetchTopic()
    }
  }

  onUploadError = async () => {
    throw new Error('Upload Failed')
  }

  onUploadAbort = async () => {
    console.warn('Upload cancelled')
  }

  handleCustomVideoSelected = async (file: File) => {
    const { elementId, topicId } = this.props
    const { name: fileName } = file

    const { data } = await apolloClientPrivate.query<
      FetchUploadCredentialsQuery,
      FetchUploadCredentialsQueryVariables
    >({
      query: FetchUploadCredentialsDocument,
      variables: {
        elementId,
        topicId,
        fileName
      }
    })
    const topic = data.topic as TopicModelAsEditor

    if (topic.draftVersion?.element) {
      const { uploadCredentials } = topic.draftVersion?.element

      if (uploadCredentials && uploadCredentials.uploadUrl) {
        const { uploadUrl, params } = uploadCredentials
        this.uploadUrl = uploadUrl as string
        this.params = params
        const filePath = params.key

        const fd = getAWSSignatureV4Form(params, file)

        const xhr = new XMLHttpRequest()

        xhr.upload.addEventListener('progress', this.onUploadProgress, false)
        xhr.addEventListener(
          'load',
          () => this.onUploadComplete(filePath),
          false
        )
        xhr.addEventListener('error', this.onUploadError, false)
        xhr.addEventListener('abort', this.onUploadAbort, false)

        xhr.open('POST', uploadUrl, true)
        xhr.send(fd)
      }
    }
  }

  onVideoReplace = async () => {
    const { elementId, topicId } = this.props

    await apolloClientPrivate.mutate<
      ReplaceElementMutation,
      ReplaceElementMutationVariables
    >({
      mutation: ReplaceElementDocument,
      variables: {
        elementId,
        topicId
      }
    })
  }

  render() {
    const { loading, editing, uploadProgress, element } = this.state

    let body

    if (loading && !editing) {
      body = <Spinner size={SpinnerSizeEnum.large} />
    } else if (this.isTranscoding) {
      body = (
        <Wrapper>
          <Row progress={100}>
            <Heading color={brandColors.green.mid}>
              <Trans>Uploading complete</Trans>
            </Heading>
          </Row>
          <Row progress={50}>
            <Heading color={brandColors.blue.mid}>
              <Trans>Processing...</Trans>
            </Heading>
            <Box maxW="540px">
              <strong>
                <Trans>
                  You can continue working as usual, the video will show once
                  done.
                </Trans>
              </strong>
              <br />
              <Trans>
                This may take a few minutes, depending on the size of the file.
              </Trans>
            </Box>
          </Row>
          <Row progress={0}>
            <Heading>
              <Trans>Done</Trans>
            </Heading>
          </Row>
        </Wrapper>
      )
    } else if (uploadProgress) {
      const uploadProgress = this.state.uploadProgress as IUploadProgress
      const { total, loaded } = uploadProgress
      const progress = Math.round((loaded / total) * 100)
      body = (
        <Wrapper>
          <Row progress={progress} withPercentage>
            <Heading color={brandColors.blue.mid}>
              <Trans>Uploading media file...</Trans>
            </Heading>
            <Box maxW="540px">
              <Trans>
                This may take a few minutes, depending on the file size
              </Trans>
            </Box>
          </Row>
          <Row progress={0}>
            <Heading>
              <Trans>Processing</Trans>
            </Heading>
          </Row>
          <Row progress={0}>
            <Heading>
              <Trans>Done</Trans>
            </Heading>
          </Row>
        </Wrapper>
      )
    } else if (editing) {
      body = (
        <>
          <CustomVideoInput onSave={this.handleCustomVideoSelected} />
          <YoutubeVideoInput onSave={this.handleYoutubeUrlSelected} />
        </>
      )
    } else if (element) {
      body = (
        <>
          {/* @ts-expect-error TODO fix typings for MediaElement */}
          <MediaElement
            {...element}
            topicId={this.props.topicId}
            shouldTrackMedia={false}
            hidePlaybackSpeedDropdown={true}
          />
          <ButtonLight
            className="edit-btn"
            onClick={() => {
              this.setState({ showReplaceModal: true })
            }}
            leftIcon={<IconRefresh />}
          >
            <Trans>Replace</Trans>
          </ButtonLight>
        </>
      )
    }

    return (
      <>
        {this.state.showReplaceModal && (
          <GeneralRemoveModal
            show={true}
            header={t`Replace element`}
            actionName={t`replace`}
            entityName={t`element`}
            confirmButton={t`Replace`}
            onDeleted={() => this.setState({ showReplaceModal: false })}
            onCanceled={() => this.setState({ showReplaceModal: false })}
            onErrorMessage={t`Error replacing`}
            onSuccessMessage={t`has been replaced`}
            operation={async () => {
              await this.onVideoReplace()
              this.props.fetchTopic()
            }}
          />
        )}
        <div
          className={classNames('VideoElement VideoElement__edit', {
            'VideoElement__edit--audio':
              !editing &&
              this.state.element &&
              this.state.element.data &&
              typeof this.state.element.data.content === 'string' &&
              this.state.element.data.content.endsWith('mp3'),
            'VideoElement__edit--transcoding': this.isTranscoding
          })}
        >
          <div className="EditModeVideo">{body}</div>
        </div>
      </>
    )
  }
}

export default EditModeVideo
